/** Shared estimated-tax payment lookups for Tax + Analytics (no circular imports). */

export const TAX_SETTINGS_STORAGE_KEY = "sfe-tax-settings";
export const TAX_SETTINGS_CHANGED_EVENT = "sfe:tax-settings-changed";
/** Analytics should select YYYY-MM when a payment is saved to a different cash month. */
export const TAX_PAYMENT_MONTH_FOCUS_EVENT = "sfe:tax-payment-month-focus";

/** Bound StockForge+ user id for scoping browser tax cache (null = signed out). */
let taxSettingsStorageUserId: string | null = null;

/** Call on login/logout/account switch before reading or writing tax settings. */
export function setTaxSettingsStorageUserId(userId: string | null): void {
  taxSettingsStorageUserId = userId && userId.trim() ? userId.trim() : null;
}

export function getTaxSettingsStorageUserId(): string | null {
  return taxSettingsStorageUserId;
}

/** Per-account localStorage key — never share `sfe-tax-settings` across users. */
export function taxSettingsStorageKey(): string {
  return taxSettingsStorageUserId
    ? `${TAX_SETTINGS_STORAGE_KEY}:${taxSettingsStorageUserId}`
    : `${TAX_SETTINGS_STORAGE_KEY}:anon`;
}

export interface TaxPaymentMonthFocusDetail {
  monthKey: string;
  year: number;
  monthIndex: number;
}

export function notifyTaxPaymentMonthFocus(detail: TaxPaymentMonthFocusDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(TAX_PAYMENT_MONTH_FOCUS_EVENT, { detail }));
}

type PaymentKind = "estimated" | "balance-due" | "extension" | "other";

interface StoredPayment {
  taxYear?: number;
  kind?: PaymentKind | string;
  date?: string;
  amount?: number;
  /** When false, payment is Tax-tab only and does not reduce Own Capital EOM. */
  fromOwnCapital?: boolean;
}

/** Tax years at/after this pay tax from the trading account by default. */
export const OWN_CAPITAL_TAX_PAYMENTS_FROM_YEAR = 2026;

/** Analytics forecast months at/after this use predicted tax as Cash Ops withdrawals. */
export const FORECAST_TAX_WITHDRAWALS_FROM_MONTH = "2027-01";

/** Fallback MMDD when payment logs have no own-capital dates to mirror. */
export const FORECAST_TAX_FALLBACK_MD = "03-30";

/** Own Capital EOM only deducts tax cash that left the trading account. */
export function paymentAffectsOwnCapital(payment: {
  kind?: PaymentKind | string;
  fromOwnCapital?: boolean;
  taxYear?: number;
}): boolean {
  if (payment.fromOwnCapital === true) return true;
  if (payment.fromOwnCapital === false) return false;
  // Default: TY2026+ from trading; earlier years (W-2 / bank) outside own capital.
  const taxYear = Number(payment.taxYear);
  if (Number.isFinite(taxYear)) return taxYear >= OWN_CAPITAL_TAX_PAYMENTS_FROM_YEAR;
  return (payment.kind ?? "estimated") === "estimated";
}

function paymentMonthIndex(date: string, year: number): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const paymentYear = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (paymentYear !== year || monthIndex < 0 || monthIndex >= 12) return null;
  return monthIndex;
}

function paymentsForYear(settings: unknown, year: number): StoredPayment[] {
  if (!settings || typeof settings !== "object") return [];
  const years = (settings as { years?: Record<string, { estimatedTaxPayments?: unknown }> })
    .years;
  const raw = years?.[String(year)]?.estimatedTaxPayments;
  return Array.isArray(raw) ? (raw as StoredPayment[]) : [];
}

function monthsForYear(settings: unknown, year: number): (number | null)[] {
  if (!settings || typeof settings !== "object") return [];
  const years = (settings as { years?: Record<string, { estimatedTaxPaidMonths?: unknown }> })
    .years;
  const raw = years?.[String(year)]?.estimatedTaxPaidMonths;
  return Array.isArray(raw) ? (raw as (number | null)[]) : [];
}

const IRS_CASH_OUTFLOW_KINDS = new Set<PaymentKind>([
  "estimated",
  "balance-due",
  "extension",
]);

function loadStoredTaxSettings(): unknown | null {
  try {
    const raw = localStorage.getItem(taxSettingsStorageKey());
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function paymentsInMonth(
  settings: unknown,
  year: number,
  monthIndex: number,
  kinds: ReadonlySet<PaymentKind> | null,
  opts: { ownCapitalOnly?: boolean } = {},
): number {
  const payments = paymentsForYear(settings, year);
  if (payments.length === 0) return 0;
  return payments
    .filter((payment) => {
      const kind = (payment.kind ?? "estimated") as PaymentKind;
      if (kinds && !kinds.has(kind)) return false;
      if (opts.ownCapitalOnly && !paymentAffectsOwnCapital(payment)) return false;
      return (
        typeof payment.date === "string" &&
        paymentMonthIndex(payment.date, year) === monthIndex &&
        Number.isFinite(Number(payment.amount))
      );
    })
    .reduce((sum, payment) => sum + Number(payment.amount), 0);
}

/** Prior-tax-year bucket payments whose cash date falls in `calendarYear`/`monthIndex`. */
function priorYearBucketPaymentsInMonth(
  settings: unknown,
  calendarYear: number,
  monthIndex: number,
  kinds: ReadonlySet<PaymentKind> | null,
  opts: { ownCapitalOnly?: boolean } = {},
): number {
  const priorTaxYear = calendarYear - 1;
  const payments = paymentsForYear(settings, priorTaxYear);
  if (payments.length === 0) return 0;
  return payments
    .filter((payment) => {
      const kind = (payment.kind ?? "estimated") as PaymentKind;
      if (kinds && !kinds.has(kind)) return false;
      if (opts.ownCapitalOnly && !paymentAffectsOwnCapital(payment)) return false;
      if (payment.taxYear != null && Number(payment.taxYear) !== priorTaxYear) return false;
      return (
        typeof payment.date === "string" &&
        paymentMonthIndex(payment.date, calendarYear) === monthIndex &&
        Number.isFinite(Number(payment.amount))
      );
    })
    .reduce((sum, payment) => sum + Number(payment.amount), 0);
}

function priorYearTotalTaxForYear(settings: unknown, year: number): number | null {
  if (!settings || typeof settings !== "object") return null;
  const years = (settings as { years?: Record<string, { priorYearTotalTax?: unknown }> }).years;
  const raw = years?.[String(year)]?.priorYearTotalTax;
  const value = Number(raw);
  return raw == null || !Number.isFinite(value) ? null : value;
}

/** Payments Jan 1–Apr 15 of `year` stored under the prior tax-year bucket. */
function q1PriorYearTaxPaidFromSettings(settings: unknown, year: number): number {
  const priorTaxYear = year - 1;
  const q1Start = `${year}-01-01`;
  const q1End = `${year}-04-15`;
  return paymentsForYear(settings, priorTaxYear)
    .filter((payment) => {
      if (typeof payment.date !== "string") return false;
      if (payment.date < q1Start || payment.date > q1End) return false;
      if (payment.taxYear != null && Number(payment.taxYear) !== priorTaxYear) return false;
      return Number.isFinite(Number(payment.amount));
    })
    .reduce((sum, payment) => sum + Number(payment.amount), 0);
}

/**
 * For future forecast tax years, unpaid prior-year tax is assumed paid on Mar 30
 * (Cash Ops withdrawal in Analytics 2027+; Tax Q1 cash plan uses the same date).
 * Returns the assumed remaining balance (0 when not a forecast year or already covered).
 */
export function forecastAssumedPriorYearSettlementAmount(
  year: number,
  priorYearTotalTax: number | null | undefined,
  q1ActualPaid: number,
): number {
  if (year <= new Date().getFullYear()) return 0;
  if (priorYearTotalTax == null || !Number.isFinite(priorYearTotalTax)) return 0;
  return Math.max(0, Number(priorYearTotalTax) - Math.max(0, q1ActualPaid));
}

function daysInMonth(year: number, month1to12: number): number {
  return new Date(year, month1to12, 0).getDate();
}

function clampIsoDate(year: number, month: number, day: number): string {
  const md = Math.min(Math.max(1, month), 12);
  const dd = Math.min(Math.max(1, day), daysInMonth(year, md));
  return `${year}-${String(md).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

function listOwnCapitalPaymentsForTaxYear(
  settings: unknown,
  taxYear: number,
): StoredPayment[] {
  return paymentsForYear(settings, taxYear).filter(
    (payment) =>
      paymentAffectsOwnCapital({
        kind: payment.kind,
        fromOwnCapital: payment.fromOwnCapital,
        taxYear: payment.taxYear ?? taxYear,
      }) &&
      typeof payment.date === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(payment.date) &&
      Number(payment.amount) > 0,
  );
}

/** Latest tax year ≤ currentYear with ≥1 own-capital payment log, else null. */
export function latestOwnCapitalPaymentSourceYear(
  settings: unknown,
  throughYear = new Date().getFullYear(),
): number | null {
  if (!settings || typeof settings !== "object") return null;
  const years = (settings as { years?: Record<string, unknown> }).years;
  if (!years) return null;
  const keys = Object.keys(years)
    .map(Number)
    .filter((y) => Number.isFinite(y) && y <= throughYear)
    .sort((a, b) => b - a);
  for (const y of keys) {
    if (listOwnCapitalPaymentsForTaxYear(settings, y).length > 0) return y;
  }
  return null;
}

export interface ForecastTaxScheduleSlot {
  /** ISO date in the forecast tax year's cash calendar. */
  date: string;
  /** Relative weight from source log amount (1 when fallback). */
  weight: number;
}

/**
 * Mirror own-capital payment log MMDD onto `forecastTaxYear` (and +1 for Jan Q4).
 * Empty logs → single Mar 30 fallback slot.
 */
export function forecastTaxWithdrawalSchedule(
  forecastTaxYear: number,
  settings: unknown = loadStoredTaxSettings(),
): ForecastTaxScheduleSlot[] {
  const sourceYear = latestOwnCapitalPaymentSourceYear(settings);
  if (sourceYear == null || !settings) {
    return [
      {
        date: clampIsoDate(
          forecastTaxYear,
          Number(FORECAST_TAX_FALLBACK_MD.slice(0, 2)),
          Number(FORECAST_TAX_FALLBACK_MD.slice(3, 5)),
        ),
        weight: 1,
      },
    ];
  }

  const slots: ForecastTaxScheduleSlot[] = [];
  for (const payment of listOwnCapitalPaymentsForTaxYear(settings, sourceYear)) {
    const taxYear = Number(payment.taxYear ?? sourceYear);
    const m = Number(payment.date!.slice(5, 7));
    const d = Number(payment.date!.slice(8, 10));
    const payYear = Number(payment.date!.slice(0, 4));
    const yearOffset = payYear - taxYear;
    const targetYear = forecastTaxYear + (Number.isFinite(yearOffset) ? yearOffset : 0);
    if (targetYear < forecastTaxYear || targetYear > forecastTaxYear + 1) continue;
    slots.push({
      date: clampIsoDate(targetYear, m, d),
      weight: Math.max(0, Number(payment.amount) || 0),
    });
  }

  if (slots.length === 0) {
    return [
      {
        date: clampIsoDate(
          forecastTaxYear,
          Number(FORECAST_TAX_FALLBACK_MD.slice(0, 2)),
          Number(FORECAST_TAX_FALLBACK_MD.slice(3, 5)),
        ),
        weight: 1,
      },
    ];
  }
  return slots;
}

export interface ForecastOwnCapitalTaxPlan {
  /** Remaining estimated / safe-harbor cash to pay from trading for this tax year. */
  inYearNeed: number;
  /** Unpaid prior-year tax settled in this calendar year (Mar 30). */
  priorYearBalance: number;
}

/**
 * Monthly predicted tax withdrawals for Own Capital (Cash Ops), keyed by YYYY-MM.
 * Only emits months at/after {@link FORECAST_TAX_WITHDRAWALS_FROM_MONTH}.
 */
export function buildPredictedTaxWithdrawalsByMonth(
  forecastTaxYear: number,
  plan: ForecastOwnCapitalTaxPlan,
  settings: unknown = loadStoredTaxSettings(),
): Record<string, number> {
  const out: Record<string, number> = {};
  const bump = (monthKey: string, amount: number): void => {
    if (!(amount > 0)) return;
    if (monthKey < FORECAST_TAX_WITHDRAWALS_FROM_MONTH) return;
    out[monthKey] = (out[monthKey] || 0) + amount;
  };

  const inYearNeed = Math.max(0, Number(plan.inYearNeed) || 0);
  if (inYearNeed > 0) {
    const slots = forecastTaxWithdrawalSchedule(forecastTaxYear, settings);
    const weightSum = slots.reduce((s, slot) => s + slot.weight, 0);
    if (weightSum > 0) {
      let allocated = 0;
      slots.forEach((slot, index) => {
        const share =
          index === slots.length - 1
            ? inYearNeed - allocated
            : Math.round(((inYearNeed * slot.weight) / weightSum) * 100) / 100;
        allocated += share;
        bump(slot.date.slice(0, 7), share);
      });
    }
  }

  const priorBalance = Math.max(0, Number(plan.priorYearBalance) || 0);
  if (priorBalance > 0) {
    const priorDate = clampIsoDate(
      forecastTaxYear,
      Number(FORECAST_TAX_FALLBACK_MD.slice(0, 2)),
      Number(FORECAST_TAX_FALLBACK_MD.slice(3, 5)),
    );
    bump(priorDate.slice(0, 7), priorBalance);
  }

  return out;
}

/** Sum of predicted tax withdrawals for one calendar month (0 before 2027-01). */
export function predictedTaxWithdrawalsForMonthKey(
  monthKey: string,
  plansByTaxYear: Map<number, ForecastOwnCapitalTaxPlan>,
  settings: unknown = loadStoredTaxSettings(),
): number {
  if (!/^\d{4}-\d{2}$/.test(monthKey) || monthKey < FORECAST_TAX_WITHDRAWALS_FROM_MONTH) {
    return 0;
  }
  const calendarYear = Number(monthKey.slice(0, 4));
  // Tax year T deposits cash in calendar T and T+1 (Q4); also prior-balance on Mar T.
  let total = 0;
  for (const taxYear of [calendarYear - 1, calendarYear]) {
    if (taxYear < 2027) continue;
    const plan = plansByTaxYear.get(taxYear);
    if (!plan) continue;
    const byMonth = buildPredictedTaxWithdrawalsByMonth(taxYear, plan, settings);
    total += byMonth[monthKey] || 0;
  }
  return total;
}

/** Merge predicted withdrawal maps for a set of forecast tax years. */
export function mergePredictedTaxWithdrawals(
  plansByTaxYear: Map<number, ForecastOwnCapitalTaxPlan>,
  settings: unknown = loadStoredTaxSettings(),
): Map<string, number> {
  const merged = new Map<string, number>();
  for (const [taxYear, plan] of plansByTaxYear) {
    if (taxYear < 2027) continue;
    const byMonth = buildPredictedTaxWithdrawalsByMonth(taxYear, plan, settings);
    for (const [monthKey, amount] of Object.entries(byMonth)) {
      merged.set(monthKey, (merged.get(monthKey) || 0) + amount);
    }
  }
  return merged;
}

export interface ForecastTaxCashFlowRow {
  flow_date: string;
  amount: number;
  idempotency_key: string;
  note: string;
  tax_year: number;
}

/** Individual forecast tax cash_flow rows (2027+) for server sync. */
export function buildForecastTaxCashFlowRows(
  plansByTaxYear: Map<number, ForecastOwnCapitalTaxPlan>,
  settings: unknown = loadStoredTaxSettings(),
): ForecastTaxCashFlowRow[] {
  const rows: ForecastTaxCashFlowRow[] = [];
  for (const [taxYear, plan] of plansByTaxYear) {
    if (taxYear < 2027) continue;

    const inYearNeed = Math.max(0, Number(plan.inYearNeed) || 0);
    if (inYearNeed > 0) {
      const slots = forecastTaxWithdrawalSchedule(taxYear, settings);
      const weightSum = slots.reduce((sum, slot) => sum + slot.weight, 0);
      if (weightSum > 0) {
        let allocated = 0;
        slots.forEach((slot, index) => {
          const share =
            index === slots.length - 1
              ? inYearNeed - allocated
              : Math.round(((inYearNeed * slot.weight) / weightSum) * 100) / 100;
          allocated += share;
          if (!(share > 0)) return;
          rows.push({
            flow_date: slot.date,
            amount: share,
            idempotency_key: `forecast:tax:${taxYear}:in-year:${slot.date}`,
            note: `Forecast TY${taxYear} estimated tax`,
            tax_year: taxYear,
          });
        });
      }
    }

    const priorBalance = Math.max(0, Number(plan.priorYearBalance) || 0);
    if (priorBalance > 0) {
      const priorDate = clampIsoDate(
        taxYear,
        Number(FORECAST_TAX_FALLBACK_MD.slice(0, 2)),
        Number(FORECAST_TAX_FALLBACK_MD.slice(3, 5)),
      );
      rows.push({
        flow_date: priorDate,
        amount: priorBalance,
        idempotency_key: `forecast:tax:${taxYear}:prior-balance:${priorDate}`,
        note: `Forecast TY${taxYear - 1} balance due`,
        tax_year: taxYear,
      });
    }
  }
  return rows;
}

function forecastAssumedPriorYearSettlementFromSettings(
  settings: unknown,
  year: number,
): number {
  return forecastAssumedPriorYearSettlementAmount(
    year,
    priorYearTotalTaxForYear(settings, year),
    q1PriorYearTaxPaidFromSettings(settings, year),
  );
}

function legacyEstimatedMonthAmount(
  settings: unknown,
  year: number,
  monthIndex: number,
): number {
  const months = monthsForYear(settings, year);
  const monthly = months[monthIndex];
  return monthly != null && Number.isFinite(Number(monthly)) ? Number(monthly) : 0;
}

/** Estimated tax paid (kind=estimated) whose cash date falls in `YYYY-MM`. */
export function estimatedTaxPaidForMonthKey(monthKey: string): number {
  const [yearRaw, monthRaw] = monthKey.split("-").map(Number);
  if (!Number.isFinite(yearRaw) || !Number.isFinite(monthRaw)) return 0;
  const monthIndex = monthRaw - 1;
  if (monthIndex < 0 || monthIndex >= 12) return 0;

  const settings = loadStoredTaxSettings();
  if (!settings) return 0;
  const payments = paymentsForYear(settings, yearRaw);
  if (payments.length > 0) {
    const fromPayments = paymentsInMonth(
      settings,
      yearRaw,
      monthIndex,
      new Set<PaymentKind>(["estimated"]),
    );
    if (fromPayments !== 0) return fromPayments;
    return legacyEstimatedMonthAmount(settings, yearRaw, monthIndex);
  }
  return legacyEstimatedMonthAmount(settings, yearRaw, monthIndex);
}

/**
 * IRS cash outflows for Own Capital (payments from the trading account).
 * Default: tax year 2026+ count; earlier years do not (unless fromOwnCapital=true).
 * Excludes kind=other. Also includes prior-tax-year bucket payments whose cash
 * date falls in this month. Assumed forecast prior-year settlements for 2027+
 * are booked as Cash Ops withdrawals instead (see mergePredictedTaxWithdrawals).
 */
export function taxCashOutflowForMonthKey(monthKey: string): number {
  const [yearRaw, monthRaw] = monthKey.split("-").map(Number);
  if (!Number.isFinite(yearRaw) || !Number.isFinite(monthRaw)) return 0;
  const monthIndex = monthRaw - 1;
  if (monthIndex < 0 || monthIndex >= 12) return 0;

  const settings = loadStoredTaxSettings();
  if (!settings) return 0;
  const payments = paymentsForYear(settings, yearRaw);
  const ownCapitalOpts = { ownCapitalOnly: true };
  const fromCalendarYear =
    payments.length > 0
      ? paymentsInMonth(
          settings,
          yearRaw,
          monthIndex,
          IRS_CASH_OUTFLOW_KINDS,
          ownCapitalOpts,
        )
      : legacyEstimatedMonthAmount(settings, yearRaw, monthIndex);
  const fromPriorYearBucket = priorYearBucketPaymentsInMonth(
    settings,
    yearRaw,
    monthIndex,
    IRS_CASH_OUTFLOW_KINDS,
    ownCapitalOpts,
  );
  // Pre-2027 only: Mar 30 assumed settlement. 2027+ uses predicted withdrawals.
  const assumedForecastSettlement =
    monthKey < FORECAST_TAX_WITHDRAWALS_FROM_MONTH && monthIndex === 2
      ? forecastAssumedPriorYearSettlementFromSettings(settings, yearRaw)
      : 0;
  return fromCalendarYear + fromPriorYearBucket + assumedForecastSettlement;
}

export function notifyTaxSettingsChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(TAX_SETTINGS_CHANGED_EVENT));
}
